import React, { useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import './LongevityChart.scss';

/**
 * 「香港在全球預期壽命中名列前茅」, an in-house replacement for the Piktochart of the same name, in
 * the look of ai.portfoplus.com: the Piktochart's grouped bars of life expectancy at birth for men
 * and women in four places, with a picker for the sex. The figures are the Piktochart's own
 * (UNDP Human Development Report 2023/24), kept as they are.
 */

type Place = 'hk' | 'ch' | 'jp' | 'es';
type Sex = 'male' | 'female';

export const PLACES: Place[] = ['hk', 'ch', 'jp', 'es'];
/** Life expectancy at birth, in years; the Piktochart's figures. */
export const AGE: Record<Sex, Record<Place, number>> = {
  male: { hk: 81.2, ch: 82.5, jp: 81.8, es: 81.2 },
  female: { hk: 86.9, ch: 85.9, jp: 87.8, es: 86.5 }
};

/** The Piktochart's axis starts at 75, so the bars show the gap rather than the whole age. */
const AXIS_MIN = 75;
const AXIS_MAX = 90;
const TICKS = [75, 77.5, 80, 82.5, 85, 87.5, 90];

const SOURCE = 'https://hdr.undp.org/content/human-development-report-2023-24';

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const pct = (age: number) => ((age - AXIS_MIN) / (AXIS_MAX - AXIS_MIN)) * 100;

const LongevityChart = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [sex, setSex] = useState<Sex>('female');

  const best = PLACES.reduce((a, b) => (AGE[sex][b] > AGE[sex][a] ? b : a));
  const gap = Math.round((AGE.female.hk - AGE.male.hk) * 10) / 10;

  return (
    <div className="longevity-chart" lang={HTML_LANG[intl.locale] || intl.locale}>
      <h2 className="lv-title">
        <span>{t('lv-title')}</span>
      </h2>

      <div className="lv-picker" role="group" aria-label={t('lv-axis')}>
        {(['male', 'female'] as Sex[]).map(s => (
          <button
            key={s}
            type="button"
            className={s === sex ? 'is-on' : ''}
            aria-pressed={s === sex}
            onClick={() => setSex(s)}
          >
            {t(`lv-${s}`)}
          </button>
        ))}
      </div>

      <section className="lv-chart">
        <p className="lv-axis-label">{t('lv-axis')}</p>
        <div className="lv-plot">
          {TICKS.map(tick => (
            <i key={tick} className="lv-tick" style={{ bottom: `${pct(tick)}%` }}>
              <span>{tick}</span>
            </i>
          ))}
          <div className="lv-bars">
            {PLACES.map(place => (
              <div key={place} className={`lv-col is-${place}${place === best ? ' is-best' : ''}`}>
                <strong>{AGE[sex][place]}</strong>
                <span className="lv-bar" style={{ height: `${pct(AGE[sex][place])}%` }} />
                <em>{t(`lv-${place}`)}</em>
              </div>
            ))}
          </div>
        </div>
        <p className="lv-note" aria-live="polite">
          {t('lv-best', { place: t(`lv-${best}`), sex: t(`lv-${sex}`), age: AGE[sex][best] })}
        </p>
      </section>

      <p className="lv-gap">{t('lv-gap', { n: gap })}</p>

      <div className="lv-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('lv-conclusion')}</p>
      </div>

      <p className="lv-source">
        {t('lv-source')}
        <a href={SOURCE} target="_blank" rel="noopener noreferrer">
          {t('lv-source-link')}
        </a>
      </p>
    </div>
  );
};

export default LongevityChart;
